import { useMutation, useQuery, useQueryClient, requestDeals } from '@workspace/api';
import { useNavigate } from 'react-router-dom';

export interface User {
    id: number;
    name: string;
    email: string;
    roles: string[];
    permissions: string[];
    belair_id?: number;
}

/** Shape de la réponse axios GET /api/auth/me */
interface AuthMeResponse {
    data: {
        data: { user: User };
    };
}

interface LoginCredentials {
    email: string;
    password: string;
}

const LOGOUT_FLAG_KEY = 'deals_just_logged_out';

export function useAuth() {
    const justLoggedOut = sessionStorage.getItem(LOGOUT_FLAG_KEY) === 'true';

    return useQuery<AuthMeResponse, Error, { user: User }>({
        queryKey: ['deals', 'auth', 'me'],
        queryFn: () => requestDeals.get('/api/auth/me'),
        select: (response) => response.data.data,
        retry: false,
        staleTime: 5 * 60 * 1000,
        enabled: !justLoggedOut,
    });
}

export function useLogin() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: (credentials: LoginCredentials) => requestDeals.post('/api/auth/login', credentials),
        onSuccess: () => {
            sessionStorage.removeItem(LOGOUT_FLAG_KEY);
            queryClient.invalidateQueries({ queryKey: ['deals', 'auth', 'me'] });
            navigate('/quotes');
        },
    });
}

export function useValidateToken() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: (accessToken: string) => requestDeals.post('/api/auth/token/validate', { access_token: accessToken }),
        onSuccess: () => {
            sessionStorage.removeItem(LOGOUT_FLAG_KEY);
            queryClient.invalidateQueries({ queryKey: ['deals', 'auth', 'me'] });
            navigate('/quotes');
        },
    });
}

export function useLogout() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const cleanup = () => {
        sessionStorage.setItem(LOGOUT_FLAG_KEY, 'true');
        queryClient.removeQueries({ queryKey: ['deals', 'auth', 'me'] });
        queryClient.clear();
        navigate('/auth/login');
    };

    return useMutation({
        mutationFn: () => requestDeals.post('/api/auth/logout'),
        onSuccess: cleanup,
        onError: cleanup,
    });
}
